import React, { useState, useEffect, useRef, RefObject } from 'react';
import { HeaderProps } from './Header.types';
import './Header.scss';
import classnames from 'classnames';
import DesktopMoreMenu from './DesktopMoreMenu';
import MobileMoreMenu from './MobileMoreMenu';
import { useMedia } from '../../hooks';
import { DisplayDimensions } from '../../constants';
import Search from './Search';
import { KeyCodes } from '../../constants/keyCodes';
import AccountMenu from './AccountMenu';

const Header = ({
  data,
  headerSearchProps,
  homeUrl = '/',
  searchBarVisible = true,
  accountButtonRequired,
  accountProps,
}: HeaderProps) => {
  const isSmallScreen = useMedia(`(max-width: ${DisplayDimensions.MOBILE}px)`);
  const isSmallScreenWithAccountButton =
    accountButtonRequired &&
    useMedia(`(max-width: ${DisplayDimensions.MOBILE_WITH_ACCOUNT}px)`);
  const isSmallDesktopScreen =
    accountButtonRequired &&
    useMedia(`(max-width: ${DisplayDimensions.ACCOUNT_BUTTON_MIN}px)`);
  const isSmallScreenLandscape = useMedia(
    `(max-height: ${DisplayDimensions.ACCOUNT_MENU_MIN_HEIGHT}px)`
  );
  const [isMenuShown, setIsMenuShown] = useState(false);
  const [isSubMenuShown, setIsSubMenuShown] = useState(false);
  const [isAccountMenuShown, setIsAccountMenuShown] = useState(false);
  const [menuIsInView, setMenuIsInView] = useState<boolean>(true);
  const accountMenuRef = useRef();
  const accountButtonRef = useRef();
  const moreLessRef = useRef();
  const roundelClass =
    data?.roundelType && ` tfl-header__roundel-${data.roundelType}`;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode === KeyCodes.Tab) {
        isSmallScreen ? handleMobileTabbing(e) : handleDesktopTabbing(e);
      }
      if (e.keyCode === KeyCodes.Escape) {
        closeAllMenus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSubMenuShown, isSmallScreen]);

  useEffect(() => {
    const headerOutWrapper = document.querySelector(
      '.tfl-header__outer-wrapper'
    ) as HTMLElement;

    // Change header from fixed to absolute to allow the menu to be scrolled
    const orientation = window.matchMedia('(orientation: landscape)');
    if (orientation.matches && isSmallScreenLandscape && !isMenuShown) {
      setMenuIsInView(false);
      headerOutWrapper?.style.setProperty('position', 'absolute');
      return;
    }

    headerOutWrapper?.style.removeProperty('position');
    setMenuIsInView(true);
  }, []);

  useEffect(() => {
    window.addEventListener('orientationchange', closeAllMenus);
    return () => window.removeEventListener('orientationchange', closeAllMenus);
  }, []);

  const toggleMoreMenu = () => {
    setIsMenuShown((isMenuShown) => !isMenuShown);
  };

  const handleOverlayClick = () => {
    setIsMenuShown(false);
  };

  const closeAllMenus = () => {
    setIsMenuShown(false);
    setIsSubMenuShown(false);
    setIsAccountMenuShown(false);
  };

  const toggleAccountMenu = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const event = e.target as HTMLElement;
    isMenuShown && setIsMenuShown(false);

    // Check if user is signed in
    if (!accountProps.isUserAuthenticated) {
      return accountProps.handleSignIn();
    }

    // Check if sign out button was clicked
    event.innerHTML == 'Sign out' && accountProps.handleSignOut();

    setIsAccountMenuShown(!isAccountMenuShown);
  };

  const handleOutsideClick = (e: MouseEvent) => {
    const accountButton = accountButtonRef.current as HTMLElement;
    const accountMenu = accountMenuRef.current as HTMLElement;
    const event = e?.target as HTMLElement;

    // Check if click occured within the account modal
    if (accountMenu && accountMenu.contains(event)) {
      return;
    }

    // Cancel outside click when using account buttons
    if (
      isAccountMenuShown &&
      event !== accountButton &&
      event !== accountMenu &&
      event.innerHTML !== 'Sign out'
    ) {
      setIsAccountMenuShown(false);
    }
  };

  const handleDesktopTabbing = (e: KeyboardEvent) => {
    const keyDownElement = e.target as HTMLElement;
    const keyDownInnerHTML = keyDownElement.innerHTML;
    const menuLinks = document.querySelectorAll(
      '.tfl-header__more-menu-desktop-link'
    );

    const lessButton = moreLessRef.current as unknown as HTMLElement;

    if (menuLinks?.length && lessButton) {
      const lastLinkIndex = menuLinks.length - 1;
      // Create Focus Loop
      if (menuLinks[lastLinkIndex].innerHTML === keyDownInnerHTML) {
        if (!e.shiftKey && e.keyCode === KeyCodes.Tab) {
          lessButton.focus();
          e.preventDefault();
        }
      }

      if (
        e.shiftKey &&
        e.keyCode === KeyCodes.Tab &&
        keyDownInnerHTML === 'Less'
      ) {
        const lastLink = menuLinks[lastLinkIndex] as HTMLElement;
        lastLink.focus();
        e.preventDefault();
      }
    }
  };

  const handleMobileTabbing = (e: KeyboardEvent) => {
    const keyDownElement = e.target as HTMLElement;
    const keyDownInnerHTML = keyDownElement.innerHTML;
    const menuButton = document.querySelector(
      '.tfl-header__mobile-menu-button'
    ) as HTMLElement;

    // Create Focus Loop - tab
    if (keyDownElement.id === 'quickLinks' && !e.shiftKey) {
      menuButton.focus();
      e.preventDefault();
    }

    // Create Focus loop - shift-tab
    if (keyDownInnerHTML === 'Close' && !isSubMenuShown) {
      if (e.shiftKey && e.keyCode === KeyCodes.Tab) {
        const lastElement = document.getElementById('quickLinks');
        lastElement?.focus();
        e.preventDefault();
      }
    }

    // Sub Menu Links
    const subMenuLinks = document.querySelectorAll(
      '.tfl-header__sub-menu-link'
    );

    // Close Button
    const closeButton = document.querySelector(
      '.tfl-header__mobile-menu-button'
    ) as HTMLElement;

    const lastLinkSubMenuIndex = subMenuLinks.length - 1;
    // Focus Loop for Mobile Sub Menu
    if (
      isSubMenuShown &&
      subMenuLinks &&
      subMenuLinks.length > 0 &&
      closeButton &&
      !e.shiftKey
    ) {
      if (subMenuLinks[lastLinkSubMenuIndex].innerHTML === keyDownInnerHTML) {
        closeButton.focus();
        e.preventDefault();
      }
    }

    if (isSubMenuShown && subMenuLinks && closeButton && e.shiftKey) {
      if (keyDownInnerHTML === 'Close') {
        const lastElem = subMenuLinks[lastLinkSubMenuIndex] as HTMLElement;
        lastElem.focus();
        e.preventDefault();
      }
    }
  };

  return (
    <>
      <div
        className={classnames([
          'tfl-header__outer-wrapper',
          {
            'tfl-header__outer-wrapper--mobile-menu-shown':
              isSmallScreen && isMenuShown,
          },
        ])}
      >
        <div className="tfl-header__inner-wrapper">
          <header role="banner" id="tfl-header" className="tfl-header">
            <nav
              id="tfl-nav"
              className={classnames([
                'tfl-header__nav-wrapper',
                {
                  'tfl-header__nav-wrapper--tablet-view':
                    isSmallScreenWithAccountButton,
                },
              ])}
            >
              <div className={'tfl-header__nav-inner'}>
                <a
                  href={homeUrl}
                  className={`tfl-header__roundel${roundelClass}`}
                  aria-label="Home page"
                ></a>
                {!isSmallScreen && !isSmallScreenWithAccountButton && (
                  <div className="tfl-header__top-nav-list-wrapper">
                    <ul className="tfl-header__top-nav-list">
                      {data?.lists.topNav.links.map(
                        (
                          link: {
                            url: string | undefined;
                            text: React.ReactNode;
                          },
                          i: string | number | null | undefined
                        ) => (
                          <li key={i} className="tfl-header__top-nav-item">
                            <a
                              href={link.url}
                              className="tfl-header__top-nav-link"
                            >
                              {link.text}
                            </a>
                          </li>
                        )
                      )}
                      <li>
                        <button
                          ref={moreLessRef as RefObject<HTMLButtonElement>}
                          className={classnames([
                            'tfl-header__desktop-menu-button',
                            `tfl-header__desktop-menu-button--${
                              isMenuShown ? 'less' : 'menu'
                            }`,
                          ])}
                          aria-expanded={isMenuShown}
                          aria-haspopup="true"
                          onClick={toggleMoreMenu}
                        >
                          {isMenuShown ? 'Less' : 'More'}
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
                {!isSmallScreen &&
                  !isSmallScreenWithAccountButton &&
                  searchBarVisible && (
                    <div className="tfl-header__search-wrapper">
                      <Search
                        isMenuShown={isMenuShown}
                        isSubMenuShown={isSubMenuShown}
                        isSmallScreen={false}
                        searchProps={headerSearchProps}
                      />
                    </div>
                  )}
                {(isSmallScreen || isSmallScreenWithAccountButton) && (
                  <button
                    className={classnames([
                      'tfl-header__mobile-menu-button',
                      `tfl-header__mobile-menu-button--${
                        isMenuShown ? 'close' : 'menu'
                      }`,
                    ])}
                    aria-expanded={isMenuShown}
                    aria-haspopup="true"
                    onClick={toggleMoreMenu}
                  >
                    {isMenuShown ? 'Close' : 'Menu'}
                  </button>
                )}
                {accountButtonRequired && (
                  <div
                    className={classnames([
                      `tfl-header__account-wrapper--${
                        isSmallScreen || isSmallScreenWithAccountButton
                          ? 'mobile'
                          : 'desktop'
                      }`,
                      {
                        'tfl-header__account-wrapper--menu-not-in-view':
                          !menuIsInView,
                      },
                      {
                        'tfl-header__account-wrapper--no-search':
                          !searchBarVisible && !isSmallScreen,
                        'tfl-header__account-wrapper--no-search-mobile':
                          !searchBarVisible && isSmallScreenWithAccountButton,
                      },
                      {
                        'tfl-header__account-wrapper--small-desktop-screen':
                          searchBarVisible && isSmallDesktopScreen,
                      },
                    ])}
                  >
                    <button
                      ref={accountButtonRef}
                      className={'tfl-header__top-nav-account-button'}
                      onClick={(e) => toggleAccountMenu(e)}
                    >
                      {accountProps.isUserAuthenticated ? 'Account' : 'Sign in'}
                    </button>
                    {isAccountMenuShown && (
                      <AccountMenu
                        accountMenuRef={accountMenuRef}
                        handleAccountAction={toggleAccountMenu}
                        handleProfileButtonClick={() => {
                          accountProps.handleProfileButtonClick();
                          setIsAccountMenuShown(false);
                        }}
                        handleDashboardButtonClick={() => {
                          accountProps.handleDashboardButtonClick();
                          setIsAccountMenuShown(false);
                        }}
                        handleOutsideClick={handleOutsideClick}
                        isSmallScreen={isSmallScreen}
                        isSmallScreenWithAccountButton={
                          isSmallScreenWithAccountButton
                        }
                        menuIsInView={menuIsInView}
                      />
                    )}
                  </div>
                )}
              </div>
            </nav>
            <div
              aria-hidden={!isMenuShown}
              className={classnames([
                'tfl-header__more-menu',
                `tfl-header__more-menu--${isMenuShown ? 'shown' : 'hidden'}`,
              ])}
            >
              {isSmallScreen || isSmallScreenWithAccountButton ? (
                <MobileMoreMenu
                  data={data}
                  isMenuShown={isMenuShown}
                  isSubMenuShown={isSubMenuShown}
                  setIsSubMenuShown={setIsSubMenuShown}
                  searchBarVisible={searchBarVisible}
                />
              ) : (
                <DesktopMoreMenu data={data} isMenuShown={isMenuShown} />
              )}
            </div>
          </header>
        </div>
      </div>
      {isMenuShown && (
        <div className="tfl-header__overlay" onClick={handleOverlayClick}></div>
      )}
    </>
  );
};

export default Header;
